import type { DealSummary } from "@edge/shared";

/**
 * Words that commonly appear in deal names but carry no identifying signal —
 * matching on them would link the wrong deal (e.g. an alert that says "refi"
 * would otherwise match every deal whose name contains "Refi"). Keep this
 * list short and easy to reason about. Phase 1 only.
 */
const DEAL_STOPWORDS = new Set([
  "refi", "loan", "term", "deal", "case", "second", "first",
  "con", "trust", "fund", "mortgage", "with", "and", "the",
]);

const MIN_TOKEN_LEN = 4;

/**
 * Best-effort: spot a known deal mentioned in alert title/context.
 *
 * Approach: tokenize each deal name on whitespace/dashes/slashes, drop very
 * short tokens and stopwords, then check which tokens appear (case-insensitive)
 * as substrings of the combined alert text. Score each deal by the LENGTH of
 * its longest matched token; highest score wins, tied scores broken by overall
 * deal-name length.
 *
 * Returns the matching deal's id, or `undefined` if no confident match.
 */
export function detectDealFromAlert(
  alertText: string,
  deals: DealSummary[],
): string | undefined {
  const haystack = alertText.toLowerCase();
  let bestId: string | undefined;
  let bestTokenLen = 0;
  let bestNameLen = 0;

  for (const deal of deals) {
    const tokens = deal.name
      .toLowerCase()
      .split(/[\s\-–—/]+/)
      .filter((t) => t.length >= MIN_TOKEN_LEN && !DEAL_STOPWORDS.has(t));

    let dealMaxLen = 0;
    for (const token of tokens) {
      if (haystack.includes(token) && token.length > dealMaxLen) {
        dealMaxLen = token.length;
      }
    }
    if (dealMaxLen === 0) continue;

    const nameLen = deal.name.length;
    if (
      dealMaxLen > bestTokenLen ||
      (dealMaxLen === bestTokenLen && nameLen > bestNameLen)
    ) {
      bestId = deal.id;
      bestTokenLen = dealMaxLen;
      bestNameLen = nameLen;
    }
  }
  return bestId;
}
